// ---------------------------------------------------------------------------
// PostHog — analítica de producto por usuario.
//
// Firebase Analytics (ver utils/analytics.ts) responde "cuánta gente hizo X".
// PostHog responde "qué hizo ESTE usuario", que es lo que Firebase/GA4 no puede
// darnos: perfil por persona, línea de tiempo de eventos, embudos y segmentación
// por propiedades arbitrarias. Los dos conviven: `utils/analytics.ts` escribe a
// ambos destinos y ningún proveedor puede romper al otro.
//
// Este módulo espeja la forma de `sentry.ts`: un único `initPostHog()` llamado
// desde main.tsx, sin React, y no-op silencioso si falta la key.
//
// La identidad del usuario NO se resuelve aquí — vive en analytics/identity.ts,
// enganchada al store de zustand.
// ---------------------------------------------------------------------------
import posthog from "posthog-js";
import { sanitizeCapturedProps } from "./utils/moneyPrivacy";
import { APP_VERSION } from "./constants/appVersion";
import { getAttribution, isPostHogClientId, posthogIdFromSearch } from "./utils/attribution";
import { BRANDS, DEFAULT_BRAND, type Brand } from "./utils/brands";

const DEFAULT_API_HOST = "https://us.i.posthog.com";
const DEFAULT_UI_HOST = "https://us.posthog.com";

// Dominios cuyo dominio registrable realmente controlamos y donde sí se puede
// escribir una cookie compartida entre subdominios.
//
// OJO: *.web.app y *.firebaseapp.com están en la Public Suffix List, así que una
// cookie con Domain=".web.app" es descartada por todos los navegadores. Activar
// `cross_subdomain_cookie` ahí no falla con error: simplemente no hace nada y
// esconde el problema. Mientras el landing viva en `wayak-landing-prod.web.app`
// y la app en `wayak-production.web.app` NO hay identidad compartida entre los
// dos. El día que se muevan a wayak-logistics.com / app.wayak-logistics.com
// (ver apps/landing/README.md) esto se activa solo, sin tocar código.
const SHARED_COOKIE_DOMAIN = /(^|\.)wayak-logistics\.com$/i;

let enabled = false;

/** ¿Se inicializó PostHog? Falso si no había key configurada. */
export const isPostHogEnabled = (): boolean => enabled;

/**
 * ¿PostHog tiene una persona identificada ahora mismo?
 *
 * Se consulta al SDK, no a una variable de módulo: la identidad de PostHog vive
 * en localStorage+cookie y SOBREVIVE a una recarga, mientras que cualquier
 * estado en memoria muere con la página. Sin esto, un logout que no alcanza a
 * ejecutarse antes de una redirección deja al siguiente usuario heredando el
 * distinct_id del anterior.
 */
export const isPostHogIdentified = (): boolean => {
  if (!enabled) return false;
  try {
    return posthog._isIdentified();
  } catch {
    return false;
  }
};

/**
 * Propiedades que se adjuntan a TODOS los eventos de la sesión.
 *
 * Se registran al init y hay que volver a registrarlas después de cada
 * `posthog.reset()`, porque reset() también las borra.
 */
// Modo Sandbox de la compañía (datos de ejemplo). Va en CADA evento para poder
// separar en PostHog la actividad de demostración de la real. `null` = todavía
// no se sabe (la API no ha respondido): no se afirma ni que sí ni que no.
let sandboxMode: boolean | null = null;

// Marca (CC-CONSOLE#1538 + #1535): wayak al iniciar; la de la compañía al iniciar sesión.
let brand: Brand = DEFAULT_BRAND;

// Grupo `company` vigente y sus propiedades (marca, licencia, autoalta,
// Sandbox): se acumulan porque llegan de lugares distintos.
let companyGroup: { id: string; props: Record<string, unknown> } | null = null;

function registerStaticSuperProperties(): void {
  posthog.register({
    app: "v5",
    app_version: APP_VERSION,
    tenant: import.meta.env.VITE_THEME || "unknown",
    brand,
    environment: import.meta.env.VITE_ENVIRONMENT || "development",
    // typeof-guard: __IS_PREVIEW__ es un `define` de vite.config.ts, y
    // vitest.config.ts es un archivo aparte que no lo declara.
    deploy_channel:
      typeof __IS_PREVIEW__ !== "undefined" && __IS_PREVIEW__
        ? "preview"
        : "standard",
    // Siempre presente, para que «no está en Sandbox» sea un valor y no un hueco.
    sandbox_mode: sandboxMode === true,
  });
}

/**
 * Fija si la sesión está en Modo Sandbox. Lo llama `analytics/sandboxAnalytics`
 * cada vez que la API dice el estado de la compañía.
 *
 * Como super propiedad viaja en todos los eventos siguientes; como propiedad de
 * persona dice el estado actual en la ficha de PostHog. Lo segundo sólo cuando
 * ya hay persona identificada (ver `registerPostHogProperties`: fijar
 * propiedades de persona sobre un anónimo lo promueve a identificado); si aún
 * no la hay, `identifyPostHog` lo manda al identificar.
 */
export const setPostHogSandboxMode = (value: boolean | null): void => {
  sandboxMode = value;
  if (!enabled) return;
  try {
    posthog.register({ sandbox_mode: value === true });
    if (value !== null && isPostHogIdentified()) {
      posthog.setPersonProperties({ sandbox_mode: value });
    }
    if (value !== null && companyGroup) updatePostHogCompanyGroup(companyGroup.id, { sandbox: value });
  } catch (error) {
    console.error("[PostHog] sandbox mode failed:", error);
  }
};

/** Para pruebas y diagnóstico. */
export const getPostHogSandboxMode = (): boolean | null => sandboxMode;

/**
 * Normaliza un pathname a un patrón de baja cardinalidad.
 *
 * La app tiene ~190 rutas y casi todas llevan un :id, así que sin esto PostHog
 * acumula decenas de miles de `$pathname` distintos y los desgloses por página
 * quedan inservibles. Guardamos el `$current_url` real y añadimos el patrón.
 */
function isIdSegment(seg: string): boolean {
  // Numerico puro: /vehiculos/12345
  if (/^[0-9]+$/.test(seg)) return true;
  // UUID
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) {
    return true;
  }
  // Auto-id de Firestore: 20 caracteres de [A-Za-z0-9] con mayusculas y
  // minusculas mezcladas (p. ej. Ne6pwRmFkr4bArJN47NZ). Un regex hexadecimal
  // NO los atrapa: llevan letras fuera de a-f, que es justo el caso real de
  // esta app y el que hacia inutil la normalizacion.
  if (/^[A-Za-z0-9_-]{15,}$/.test(seg) && /[0-9]/.test(seg) && /[A-Za-z]/.test(seg)) {
    return true;
  }
  return false;
}

export function normalizePath(pathname: string): string {
  return pathname
    .split("/")
    .map((seg) => (isIdSegment(seg) ? ":id" : seg))
    .join("/");
}

/**
 * ¿Con qué `distinct_id` arrancar? (contrato PostHog §1). Si la persona viene
 * de la landing (`?ph_did=` o la atribución guardada) y este navegador todavía
 * no tiene a nadie identificado, se continúa a ESA persona en vez de abrir un
 * anónimo nuevo: lo que hizo en la landing y lo que haga aquí quedan juntos, y
 * el `identify(uid)` del alta los une con la cuenta.
 *
 * Nunca pisa a alguien identificado (un usuario con sesión que abre un enlace
 * de la landing sigue siendo él). Se mira la persistencia del SDK
 * (`ph_<key>_posthog`) porque aquí PostHog aún no está iniciado.
 */
export function bootstrapDistinctId(key: string, search: string = window.location.search): string | undefined {
  const stored = getAttribution()?.ph_distinct_id;
  const fromLanding = posthogIdFromSearch(search) || (isPostHogClientId(stored) ? stored.toLowerCase() : undefined);
  // Sólo se continúa a una persona ANÓNIMA con id de posthog-js (UUID). Un
  // `lead:<sha>` ya está identificado y su unión con la cuenta la hace el CRM
  // central (CC-CONSOLE#1542), nunca el navegador.
  if (!fromLanding) return undefined;
  // Si este navegador YA tiene una persona de PostHog (identificada o
  // anónima, aunque sea otra), no se cambia: un enlace no puede reasignar la
  // historia de quien ya navega aquí. La persistencia es localStorage+cookie.
  if (storedPostHogState(key)) return undefined;
  return fromLanding;
}

/** El estado guardado del SDK (`ph_<key>_posthog`) en localStorage o cookie, o null. */
function storedPostHogState(key: string): Record<string, unknown> | null {
  const name = `ph_${key}_posthog`;
  const parse = (raw: string | null | undefined): Record<string, unknown> | null => {
    if (!raw) return null;
    try {
      const state = JSON.parse(raw) as Record<string, unknown>;
      return state && typeof state.distinct_id === "string" && state.distinct_id ? state : null;
    } catch {
      return null;
    }
  };
  try {
    const fromStorage = parse(window.localStorage.getItem(name));
    if (fromStorage) return fromStorage;
  } catch {
    /* sin localStorage: se mira la cookie */
  }
  try {
    const pair = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
    return parse(pair ? decodeURIComponent(pair.slice(name.length + 1)) : null);
  } catch {
    return null;
  }
}

export const initPostHog = (): void => {
  // La key se lee DENTRO de la función (a diferencia de sentry.ts, que la lee a
  // nivel de módulo) para que los tests puedan usar vi.stubEnv sin resetModules.
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) {
    console.warn("PostHog key not configured. Product analytics disabled.");
    return;
  }

  // La de la compañía llega al iniciar sesión (`setPostHogBrand`).
  brand = DEFAULT_BRAND;
  const bootstrapId = bootstrapDistinctId(key);

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || DEFAULT_API_HOST,
    // Anónimo a propósito (isIdentifiedID false): el `identify(uid)` del alta
    // es el que lo promueve, y la sincronía de identidad no lo resetea.
    ...(bootstrapId ? { bootstrap: { distinctID: bootstrapId } } : {}),
    ui_host: DEFAULT_UI_HOST,

    // Autocapture es la razón de ser de este cambio: hoy el ÚNICO lugar
    // instrumentado a mano es el sidebar. Autocapture cubre las otras ~189
    // rutas gratis y de forma retroactiva.
    autocapture: true,

    // Apagado a propósito: AnalyticsProvider ya emite una vista por página
    // lógica con nombre legible. El modo automático dispara también en cada
    // cambio de query param (?tab=, ?page=), lo que inflaría vistas, sesiones
    // y factura varias veces.
    capture_pageview: false,
    // Explícito: el valor por defecto es 'if_capture_pageview', que al apagar
    // lo de arriba desactivaría $pageleave y perderíamos duración de sesión.
    capture_pageleave: true,

    // Solo los usuarios identificados crean perfil de persona. La app es 100%
    // autenticada, así que no perdemos nada y evitamos pagar perfil por cada
    // bot y cada rebote en /login.
    person_profiles: "identified_only",

    // Sentry ya graba replays (10% en prod). Un segundo grabador sería
    // redundante y caro.
    disable_session_recording: true,
    disable_surveys: true,

    // Explícito y a propósito: con solo cookie, el fallo de la cookie
    // cross-subdomain en *.web.app haría que se regenerara un distinct_id
    // anónimo en cada carga, inflando "usuarios únicos".
    persistence: "localStorage+cookie",
    cross_subdomain_cookie: SHARED_COOKIE_DOMAIN.test(window.location.hostname),

    // No enmascaramos todo: eso reduciría autocapture a "alguien hizo clic en
    // algún botón". El texto se recorta en before_send (ver abajo).
    mask_all_text: false,
    mask_all_element_attributes: false,

    before_send: (event) => {
      if (!event) return event;
      const props = event.properties as Record<string, unknown> | undefined;
      if (props) {
        if (typeof props.$pathname === "string") {
          props.path_pattern = normalizePath(props.$pathname);
        }
        // La página que se ACABA de dejar (PostHog la manda cruda, con los ids
        // dentro, junto con cuánto se estuvo en ella). Sin normalizarla, el
        // «tiempo por página» sale partido en una fila por id.
        if (typeof props.$prev_pageview_pathname === "string") {
          props.prev_path_pattern = normalizePath(props.$prev_pageview_pathname);
        }
        // El texto capturado por autocapture puede contener placas, nombres de
        // conductores y de clientes, folios e importes. Se recorta y se le quitan
        // los importes en los tres sitios donde viaja (`$elements`, `$el_text` y
        // `$elements_chain`), y también en los atributos con texto. Recortar sólo
        // `$elements`, como se hacía, no protegía nada: PostHog Cloud manda
        // únicamente `$elements_chain`.
        sanitizeCapturedProps(props);
      }
      return event;
    },

    loaded: () => {
      if (import.meta.env.DEV) posthog.debug();
    },
  });

  enabled = true;
  registerStaticSuperProperties();
};

// --- Primitivas delgadas que nunca lanzan -----------------------------------
// utils/analytics.ts y analytics/identity.ts solo hablan con estas, para que un
// fallo de PostHog jamás pueda tumbar el camino de Firebase ni la app.

export const capturePostHogEvent = (
  name: string,
  props?: Record<string, unknown>,
): void => {
  if (!enabled) return;
  try {
    posthog.capture(name, props);
  } catch (error) {
    console.error("[PostHog] capture failed:", error);
  }
};

/**
 * Registra super propiedades (se adjuntan a los eventos siguientes).
 *
 * Deliberadamente NO usamos `posthog.setPersonProperties()` aquí: con
 * `person_profiles: "identified_only"`, llamarlo sobre un visitante anónimo lo
 * promueve a persona identificada y anula el ajuste. Las propiedades de persona
 * se fijan en un único lugar: `identifyPostHog`.
 */
export const registerPostHogProperties = (
  props: Record<string, unknown>,
): void => {
  if (!enabled) return;
  try {
    posthog.register(props);
  } catch (error) {
    console.error("[PostHog] register failed:", error);
  }
};

export interface PostHogIdentity {
  id: string;
  companyId?: string;
  companyName?: string;
  roleName?: string;
  roleId?: string;
  yardId?: string;
  emailDomain?: string;
  email?: string;
  name?: string;
}

export const identifyPostHog = (identity: PostHogIdentity): void => {
  if (!enabled) return;
  try {
    posthog.identify(
      identity.id,
      {
        // $set — estado actual; se sobrescribe en cada login para que siga al
        // usuario cuando cambia de rol, empresa o patio.
        company_id: identity.companyId,
        company_name: identity.companyName,
        role: identity.roleName,
        role_id: identity.roleId,
        yard_id: identity.yardId,
        email_domain: identity.emailDomain,
        // `email` y `name` son las propiedades con las que PostHog rotula la
        // ficha de una persona en su interfaz y por las que permite buscar.
        // Sin ellas la lista de personas es una columna de uids.
        ...(identity.email ? { email: identity.email } : {}),
        ...(identity.name ? { name: identity.name } : {}),
        app_version: APP_VERSION,
        tenant: import.meta.env.VITE_THEME || "unknown",
        // Sólo si ya se sabe: no se pisa el valor guardado con un «no sé».
        ...(sandboxMode !== null ? { sandbox_mode: sandboxMode } : {}),
      },
      {
        // $set_once — atributos de adquisición que nunca se deben pisar.
        first_seen_at: new Date().toISOString(),
        initial_company_id: identity.companyId,
        initial_role: identity.roleName,
      },
    );

    // Analítica por empresa: retención, adopción y embudos por cliente sin
    // tener que filtrar a mano en cada insight.
    if (identity.companyId) {
      updatePostHogCompanyGroup(identity.companyId, {
        name: identity.companyName ?? identity.companyId,
      });
    }
  } catch (error) {
    console.error("[PostHog] identify failed:", error);
  }
};

/**
 * Propiedades del grupo `company` (contrato §3: `brand`, `license`,
 * `selfServe`, `sandbox`). Se fusionan con las ya conocidas de la misma
 * compañía. Nunca lanza.
 */
export const updatePostHogCompanyGroup = (
  companyId: string | null | undefined,
  props: Record<string, unknown>,
): void => {
  if (!enabled || !companyId) return;
  try {
    const base = companyGroup && companyGroup.id === companyId ? companyGroup.props : {};
    const clean = Object.fromEntries(Object.entries(props).filter(([, v]) => v !== undefined));
    companyGroup = { id: companyId, props: { brand, ...base, ...clean } };
    posthog.group("company", companyId, companyGroup.props);
  } catch (error) {
    console.error("[PostHog] group failed:", error);
  }
};

/** `distinct_id` vigente (para mandarlo al API y unir eventos de servidor), o undefined. */
export const getPostHogDistinctId = (): string | undefined => {
  if (!enabled) return undefined;
  try {
    const id = posthog.get_distinct_id();
    // Sólo el anónimo (UUID): con sesión el id es el uid y el API no lo acepta de un cliente.
    return isPostHogClientId(id) ? id : undefined;
  } catch {
    return undefined;
  }
};

/** La compañía dijo su marca (`companies.brand`): pasa a ser la de todos los eventos. */
export const setPostHogBrand = (value: unknown): void => {
  // Sólo una marca de la lista (utils/brands, CC-CONSOLE#1535); un valor
  // ausente o desconocido no pisa la que ya se sabe.
  const raw = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (!(BRANDS as readonly string[]).includes(raw)) return;
  const next = raw as Brand;
  brand = next;
  if (!enabled) return;
  try {
    posthog.register({ brand });
    if (companyGroup) updatePostHogCompanyGroup(companyGroup.id, { brand });
  } catch (error) {
    console.error("[PostHog] brand failed:", error);
  }
};


/**
 * Corta el vínculo con la persona identificada y genera un id anónimo nuevo.
 *
 * Es lo que evita que, en una máquina compartida (caseta, despacho, turnos), el
 * siguiente usuario herede el distinct_id del anterior y sus eventos se fusionen
 * en la persona equivocada — una fusión que PostHog no permite deshacer.
 *
 * Usamos reset() y no reset(true): el segundo también rota el $device_id y
 * perderíamos la señal de "misma máquina física" sin ganar nada.
 */
export const resetPostHog = (): void => {
  if (!enabled) return;
  try {
    posthog.reset();
    // El Sandbox es de la compañía de quien salió: el siguiente empieza sin saberse.
    sandboxMode = null;
    companyGroup = null;
    // reset() también borra las super propiedades. Sin esto, cada evento
    // posterior al logout perdería tenant/environment/app_version y quedaría
    // imposible de filtrar.
    registerStaticSuperProperties();
  } catch (error) {
    console.error("[PostHog] reset failed:", error);
  }
};

/** Solo para tests: devuelve el módulo a su estado sin inicializar. */
export const _resetPostHogModuleForTests = (): void => {
  enabled = false;
  sandboxMode = null;
  brand = DEFAULT_BRAND;
  companyGroup = null;
};

export { posthog };
